import type { IOfficesSchedules, ISchedule } from "@gettactic/api";

/** Rows per request — the server's maximum, to keep round trips down. */
export const SCHEDULE_PAGE_SIZE = 500;
/**
 * Safety stop. A recurring booking materialises one row per occurrence, so a
 * daily series over a year is already hundreds of rows; this bounds a runaway
 * response without silently returning half a list.
 */
export const SCHEDULE_MAX_PAGES = 6;

export interface CollectedSchedules {
  schedules: ISchedule[];
  /**
   * True when there are more reservations than we were willing to fetch.
   * Callers that act on what they list MUST surface this rather than treating
   * a partial list as the whole set.
   */
  isTruncated: boolean;
}

/** The subset of the API response shape this needs. */
interface SchedulePageResponse {
  ok: boolean;
  result: IOfficesSchedules | null;
}

/**
 * Follows the cursor across schedule pages and returns everything.
 *
 * Throws when a page comes back not-ok. That matters more than it looks: these
 * lists drive destructive flows, and a half-fetched list rendered as if it were
 * complete would cancel some reservations while quietly leaving others behind.
 * Failing loudly lets the caller block instead.
 */
export const collectSchedulePages = async (
  fetchPage: (cursor: string | undefined) => Promise<SchedulePageResponse>
): Promise<CollectedSchedules> => {
  const schedules: ISchedule[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < SCHEDULE_MAX_PAGES; page++) {
    const response = await fetchPage(cursor);
    if (!response.ok) {
      throw new Error("Failed to load reservations");
    }
    schedules.push(...(response.result?.elements ?? []));
    cursor = response.result?.cursor || undefined;
    if (!cursor) {
      return { schedules, isTruncated: false };
    }
  }

  return { schedules, isTruncated: true };
};
